import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from './baseQuery';
import { 
  IntelligencePointCrudDto, 
  GetIntelligencePointsByStoreParams, 
  IntelligencePointViewDto,
} from '../types/intelligencePoint';
import { PagedData } from 'src/types/common';

export const intelligencePointAPI = createApi({
  reducerPath: 'intelligencePointAPI',
  baseQuery: axiosBaseQueryWithReauth({
    baseUrl: process.env.REACT_APP_API_URL
  }),
  tagTypes: ['IntelligencePoint'],
  endpoints: (builder) => ({
    getIntelligencePointsByStore: builder.query<
      PagedData<IntelligencePointViewDto>,
      GetIntelligencePointsByStoreParams
    >({
      query: ({ storeId, page, pageSize, orderBy, searchTerm, filters }) => ({
        url: `Stores/${storeId}/intelligencePoints`,
        method: 'GET',
        params: {
          page,
          pageSize,
          orderBy,
          searchTerm,
          filters: filters ? JSON.stringify(filters) : undefined
        }
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({ type: 'IntelligencePoint' as const, id })),
              { type: 'IntelligencePoint', id: 'LIST' }
            ]
          : [{ type: 'IntelligencePoint', id: 'LIST' }]
    }),

    getIntelligencePoint: builder.query<IntelligencePointViewDto, number>({
      query: (id) => ({
        url: `IntelligencePoints/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'IntelligencePoint', id }]
    }),

    createIntelligencePoint: builder.mutation<IntelligencePointViewDto, IntelligencePointCrudDto>({
      query: (dto) => ({
        url: 'IntelligencePoints',
        method: 'POST',
        body: dto
      }),
      invalidatesTags: [{ type: 'IntelligencePoint', id: 'LIST' }]
    }),

    updateIntelligencePoint: builder.mutation<
      IntelligencePointViewDto,
      { id: number; dto: IntelligencePointCrudDto }
    >({
      query: ({ id, dto }) => ({
        url: `IntelligencePoints/${id}`,
        method: 'PUT',
        body: dto
      }),
      async onQueryStarted({ id, dto }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          intelligencePointAPI.util.updateQueryData('getIntelligencePoint', id, (draft) => {
            Object.assign(draft, dto);
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'IntelligencePoint', id },
        { type: 'IntelligencePoint', id: 'LIST' }
      ]
    }),

    deleteIntelligencePoint: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `IntelligencePoints/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'IntelligencePoint', id: 'LIST' }]
    })
  })
});

export const {
  useGetIntelligencePointsByStoreQuery,
  useLazyGetIntelligencePointsByStoreQuery,
  useGetIntelligencePointQuery,
  useCreateIntelligencePointMutation,
  useUpdateIntelligencePointMutation,
  useDeleteIntelligencePointMutation
} = intelligencePointAPI;
